import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import {
  IMPORT_ACCOUNT_BATCH_EXCEEDED_MESSAGE,
  IMPORT_ACCOUNT_MAX_BATCH_ROWS,
  IMPORT_ACCOUNT_MAX_BULK_MAPPINGS,
} from '../../shared/importAccountConstants';
import type {
  ImportAccountExecuteResponse,
  ImportAccountFileUploadResponse,
  ImportAccountParseResponse,
  ImportAccountPreMappedEntry,
  ImportAccountPreviewResponse,
  ImportAccountRow,
  ImportAccountUsernameMapping,
} from '../../shared/importAccountTypes';
import { isImportRowPostable } from '../../shared/importAccountTypes';

export type ImportAccountPreviewRequestRow = {
  rowIndex: number;
  loginId: string;
  displayName: string;
  /** Client (P/L) from file — sent as `available` for import amount calculation. */
  available: number;
  percentage: number;
  matchedPartyId: number;
  excluded: boolean;
  rememberMapping: boolean;
};

export interface ImportAccountRequestPayload {
  importAccountFileId: number;
  settlementPartyId: number;
  recordDate: string;
  remark: string;
  clientBatchId: string;
  partnerPartyCode?: string;
  rows: ImportAccountPreviewRequestRow[];
}

export interface BulkUsernameMappingItem {
  externalUsername: string;
  externalDisplayName?: string;
  partyId: number;
}

function parseErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const maybeError = error as { error?: unknown; message?: unknown };
    if (typeof maybeError.error === 'string' && maybeError.error.trim()) return maybeError.error;
    if (typeof maybeError.message === 'string' && maybeError.message.trim()) return maybeError.message;
  }
  return 'Request failed. Please try again.';
}

export function assertImportBatchRowCount(count: number): void {
  if (count > IMPORT_ACCOUNT_MAX_BATCH_ROWS) {
    throw new Error(IMPORT_ACCOUNT_BATCH_EXCEEDED_MESSAGE);
  }
}

export function buildImportRequestRows(rows: ImportAccountRow[]): ImportAccountPreviewRequestRow[] {
  assertImportBatchRowCount(rows.length);
  const postableRows = rows.filter(isImportRowPostable);
  if (postableRows.length === 0) {
    throw new Error('No rows with non-zero Client (P/L) and valid partner % to import.');
  }
  return postableRows.map((row) => ({
    rowIndex: row.rowIndex,
    loginId: row.loginId,
    displayName: row.displayName,
    available: row.clientPl,
    percentage: Number(row.percentage),
    matchedPartyId: row.matchedPartyId as number,
    excluded: false,
    rememberMapping: true,
  }));
}

export function buildBulkMappingItems(rows: ImportAccountRow[]): BulkUsernameMappingItem[] {
  const byLogin = new Map<string, BulkUsernameMappingItem>();
  for (const row of rows) {
    if (!row.matchedPartyId || !row.loginId.trim()) continue;
    byLogin.set(row.loginId.trim().toLowerCase(), {
      externalUsername: row.loginId.trim(),
      externalDisplayName: row.displayName.trim() || undefined,
      partyId: row.matchedPartyId,
    });
  }
  const items = Array.from(byLogin.values());
  if (items.length > IMPORT_ACCOUNT_MAX_BULK_MAPPINGS) {
    throw new Error(`Bulk mapping exceeds maximum of ${IMPORT_ACCOUNT_MAX_BULK_MAPPINGS} entries`);
  }
  return items;
}

export async function uploadImportAccountFile(file: File): Promise<ImportAccountFileUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  try {
    return await apiService.post<ImportAccountFileUploadResponse>(
      apiRoutes.transactions.importAccountFileUpload,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  } catch (error) {
    throw new Error(parseErrorMessage(error));
  }
}

export async function parseImportAccountRows(
  fileId: number,
  rows: ImportAccountRow[],
): Promise<ImportAccountParseResponse> {
  assertImportBatchRowCount(rows.length);
  try {
    return await apiService.post<ImportAccountParseResponse>(
      apiRoutes.transactions.importAccountFileParse(fileId),
      {
        rows: rows.map((row) => ({
          rowIndex: row.rowIndex,
          userNameRaw: row.userNameRaw,
          loginId: row.loginId,
          displayName: row.displayName,
          cr: row.cr,
          dr: row.dr,
          clientPl: row.clientPl,
          exposure: row.exposure,
          available: row.available,
          accountType: row.accountType,
        })),
      },
    );
  } catch (error) {
    throw new Error(parseErrorMessage(error));
  }
}

function mapUsernameMappingItem(item: ImportAccountUsernameMapping): ImportAccountPreMappedEntry {
  const loginId = String(item.externalUsername ?? '').trim();
  const displayName = String(item.externalDisplayName ?? '').trim();
  return {
    loginId,
    userNameRaw: displayName ? `${loginId} (${displayName})` : loginId,
    partyCode: String(item.partyCode ?? ''),
    partyId: item.partyId,
    partyName: item.partyName,
  };
}

export async function listImportAccountUsernameMappings(): Promise<ImportAccountPreMappedEntry[]> {
  try {
    const response = await apiService.get<ImportAccountUsernameMapping[]>(
      apiRoutes.transactions.importAccountUsernameMappings,
    );
    if (!Array.isArray(response)) return [];
    return response
      .filter((item) => item.partyId && item.externalUsername)
      .map(mapUsernameMappingItem)
      .filter((item) => item.partyCode || item.partyId);
  } catch {
    return [];
  }
}

export async function lookupImportAccountUsernameMapping(
  username: string,
): Promise<ImportAccountPreMappedEntry | null> {
  if (!username.trim()) return null;
  try {
    const response = await apiService.get<ImportAccountUsernameMapping>(
      apiRoutes.transactions.importAccountUsernameMappingLookup(username.trim()),
    );
    if (!response?.partyId || !response.externalUsername) return null;
    return mapUsernameMappingItem(response);
  } catch {
    return null;
  }
}

export async function bulkUpsertImportAccountUsernameMappings(
  mappings: BulkUsernameMappingItem[],
): Promise<void> {
  if (mappings.length === 0) return;
  if (mappings.length > IMPORT_ACCOUNT_MAX_BULK_MAPPINGS) {
    throw new Error(`Bulk mapping exceeds maximum of ${IMPORT_ACCOUNT_MAX_BULK_MAPPINGS} entries`);
  }
  try {
    await apiService.post(apiRoutes.transactions.importAccountUsernameMappingsBulk, { mappings });
  } catch (error) {
    throw new Error(parseErrorMessage(error));
  }
}

export async function previewImportAccount(
  payload: ImportAccountRequestPayload,
): Promise<ImportAccountPreviewResponse> {
  assertImportBatchRowCount(payload.rows.length);
  try {
    return await apiService.post<ImportAccountPreviewResponse>(
      apiRoutes.transactions.importAccountPreview,
      payload,
    );
  } catch (error) {
    throw new Error(parseErrorMessage(error));
  }
}

export async function executeImportAccount(
  payload: ImportAccountRequestPayload,
): Promise<ImportAccountExecuteResponse> {
  assertImportBatchRowCount(payload.rows.length);
  try {
    return await apiService.post<ImportAccountExecuteResponse>(
      apiRoutes.transactions.importAccountExecute,
      { ...payload, confirmPreview: true },
    );
  } catch (error) {
    throw new Error(parseErrorMessage(error));
  }
}
